/* ============================================================
   KIMAIAN — Premium Enhancement JavaScript
   Scroll Reveals, Image Crossfade, Add-to-Cart Feedback,
   Stock Urgency, Waitlist, Parallax, Smooth Transitions
   ============================================================ */

(function() {
  'use strict';

  /* ----------------------------------------------------------
     1. Scroll Reveal — IntersectionObserver
     ---------------------------------------------------------- */
  function initScrollReveal() {
    const revealElements = document.querySelectorAll(
      '.km-reveal, .km-reveal-left, .km-reveal-right, .km-reveal-scale'
    );

    if (!revealElements.length) return;

    // Respect prefers-reduced-motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      revealElements.forEach(el => el.classList.add('km-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('km-visible');
          observer.unobserve(entry.target); // Only animate once
        }
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -40px 0px'
    });

    revealElements.forEach(el => observer.observe(el));
  }

  /* ----------------------------------------------------------
     2. Product Card Image Crossfade
     Reads data-hover-src on .km-card-image-wrap and injects
     a second <img> for hover swap.
     ---------------------------------------------------------- */
  function initImageCrossfade() {
    document.querySelectorAll('.km-card-image-wrap[data-hover-src]').forEach(wrap => {
      const hoverSrc = wrap.getAttribute('data-hover-src');
      if (!hoverSrc) return;

      const primaryImg = wrap.querySelector('img');
      if (!primaryImg) return;

      primaryImg.classList.add('km-primary-img');

      const hoverImg = document.createElement('img');
      hoverImg.classList.add('km-hover-img');
      hoverImg.src = hoverSrc;
      hoverImg.alt = primaryImg.alt;
      hoverImg.loading = 'lazy';
      wrap.appendChild(hoverImg);
    });
  }

  /* ----------------------------------------------------------
     3. Add-to-Cart Button Feedback
     Intercepts Shopify's add-to-cart and shows success state.
     ---------------------------------------------------------- */
  function initAddToCartFeedback() {
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('.km-btn-add-cart');
      if (!btn) return;

      // Check if it's a real form submit
      const form = btn.closest('form[action*="/cart/add"]');
      if (!form) return;

      // After a short delay (let Shopify process), show success
      setTimeout(() => {
        const originalText = btn.textContent;
        btn.classList.add('km-added');
        btn.textContent = 'Added to bag';

        setTimeout(() => {
          btn.classList.remove('km-added');
          btn.textContent = originalText;
        }, 2000);
      }, 300);
    });
  }

  /* ----------------------------------------------------------
     4. Auto-apply scroll reveal classes to Shopify sections
     Scans common Shopify theme selectors and adds .km-reveal.
     ---------------------------------------------------------- */
  function autoApplyReveal() {
    const selectors = [
      // Common Shopify Dawn / theme selectors
      '.collection-product-card',
      '.product-card-wrapper',
      '.card-wrapper',
      '.grid__item .card',
      // Section titles
      '.section-header',
      '.collection-hero',
      // Featured collections
      '.featured-collection .grid__item',
      // Rich text / image-with-text
      '.image-with-text',
      '.rich-text',
      // Product recommendations
      '.product-recommendations .grid__item',
      // Newsletter
      '.newsletter',
      // Footer blocks
      '.footer-block'
    ];

    const allElements = document.querySelectorAll(selectors.join(', '));
    allElements.forEach(el => {
      if (!el.classList.contains('km-reveal') &&
          !el.classList.contains('km-reveal-left') &&
          !el.classList.contains('km-reveal-right')) {
        el.classList.add('km-reveal');
      }
    });
  }

  /* ----------------------------------------------------------
     5. Stock Urgency Indicator
     Reads inventory_quantity from Shopify's product JSON
     and shows dynamic urgency messaging.
     ---------------------------------------------------------- */
  function initStockUrgency() {
    const container = document.querySelector('.km-stock-urgency');
    if (!container) return;

    // Try to read from Shopify's product JSON (injected in Liquid)
    const productJson = document.getElementById('km-product-json');
    if (!productJson) return;

    try {
      const product = JSON.parse(productJson.textContent);
      const variant = product.variants && product.variants[0];
      if (!variant) return;

      const qty = variant.inventory_quantity;
      const dot = container.querySelector('.km-stock-dot');
      const text = container.querySelector('.km-stock-text');

      if (qty <= 0) {
        container.style.display = 'none';
        return;
      }

      if (qty <= 3) {
        if (dot) dot.classList.add('km-low');
        if (text) text.textContent = `Only ${qty} left — Don't miss out`;
      } else if (qty <= 10) {
        if (text) text.textContent = `Limited stock — ${qty} remaining`;
      } else {
        if (text) text.textContent = 'In stock — Ready to ship';
      }
    } catch(e) {
      // Silently fail
    }
  }

  /* ----------------------------------------------------------
     6. Waitlist Form Handler
     ---------------------------------------------------------- */
  function initWaitlist() {
    document.querySelectorAll('.km-waitlist-form').forEach(form => {
      form.addEventListener('submit', function(e) {
        e.preventDefault();

        const input = form.querySelector('.km-waitlist-input');
        const email = input ? input.value.trim() : '';

        if (!email || !email.includes('@')) return;

        // Show success message
        const wrapper = form.closest('.km-waitlist');
        if (wrapper) {
          form.style.display = 'none';
          const success = wrapper.querySelector('.km-waitlist-success');
          if (success) {
            success.style.display = 'block';
            success.textContent = 'You\'re on the list! We\'ll notify you when this piece returns.';
          }
        }

        // If Klaviyo is available, subscribe to Back in Stock
        if (typeof _learnq !== 'undefined') {
          _learnq.push(['identify', { '$email': email }]);
          _learnq.push(['track', 'Waitlist Signup', {
            'ProductName': document.querySelector('.km-waitlist')?.dataset?.product || '',
            'ProductURL': window.location.href
          }]);
        }
      });
    });
  }

  /* ----------------------------------------------------------
     7. Subtle Parallax on Hero
     ---------------------------------------------------------- */
  function initParallax() {
    const hero = document.querySelector('.km-hero-parallax img');
    if (!hero) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.pageYOffset;
          const rate = scrolled * 0.3;
          hero.style.transform = `translateY(${rate}px)`;
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /* ----------------------------------------------------------
     8. Smooth Page Transition on Internal Links
     ---------------------------------------------------------- */
  function initPageTransitions() {
    // Add entrance animation to main content
    const main = document.querySelector('main') || document.querySelector('#MainContent');
    if (main) {
      main.classList.add('km-page-enter');
    }
  }

  /* ----------------------------------------------------------
     9. Luxury Gold Cursor Trail (subtle, desktop only)
     ---------------------------------------------------------- */
  function initCursorTrail() {
    if ('ontouchstart' in window) return; // Skip on touch devices
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.innerWidth < 1024) return;

    const trail = document.createElement('div');
    trail.style.cssText = `
      position: fixed;
      width: 6px;
      height: 6px;
      background: var(--km-gold, #B8965A);
      border-radius: 50%;
      pointer-events: none;
      z-index: 99999;
      opacity: 0;
      transition: opacity 0.3s ease;
      mix-blend-mode: screen;
    `;
    document.body.appendChild(trail);

    let mouseX = 0, mouseY = 0;
    let trailX = 0, trailY = 0;

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      trail.style.opacity = '0.6';
    });

    document.addEventListener('mouseleave', () => {
      trail.style.opacity = '0';
    });

    function animate() {
      trailX += (mouseX - trailX) * 0.15;
      trailY += (mouseY - trailY) * 0.15;
      trail.style.left = trailX - 3 + 'px';
      trail.style.top = trailY - 3 + 'px';
      requestAnimationFrame(animate);
    }
    animate();
  }

  /* ----------------------------------------------------------
     INIT — Run everything on DOM ready
     ---------------------------------------------------------- */
  function init() {
    autoApplyReveal();
    initScrollReveal();
    initImageCrossfade();
    initAddToCartFeedback();
    initStockUrgency();
    initWaitlist();
    initParallax();
    initPageTransitions();
    initCursorTrail();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
